import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PortalQuoteResponseType, resolveRegionSafe } from 'stringline-types';
import { PortalApiError, fetchPortalQuote } from '../api/portal';
import AlreadyResponded from '../components/AlreadyResponded';
import Attachments from '../components/Attachments';
import Button from '../components/Button';
import Card from '../components/Card';
import LineItems from '../components/LineItems';
import QuoteHeader from '../components/QuoteHeader';
import ResponseForm, { ResponseMode } from '../components/ResponseForm';
import { RichText } from '../components/RichText';
import Spinner from '../components/Spinner';
import ExpiredOrInvalid from './ExpiredOrInvalid';

type QuoteData = PortalQuoteResponseType['data'];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: QuoteData }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

const QuoteView = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [activeMode, setActiveMode] = useState<ResponseMode | null>(null);
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: 'error', message: 'Missing quote token.' });
      return () => {
        cancelled = true;
      };
    }
    setState({ kind: 'loading' });
    fetchPortalQuote(token)
      .then((res) => {
        if (cancelled) return;
        setState({ kind: 'loaded', data: res.data });
        const initialSelected = new Set(
          res.data.lineItems.filter((item) => !item.isOptional).map((item) => item.id)
        );
        setSelectedLineItemIds(initialSelected);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof PortalApiError && err.status === 404) {
          setState({ kind: 'expired' });
          return;
        }
        const message =
          err instanceof Error ? err.message : 'We could not load this quote. Please try again.';
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleToggleItem = useCallback((id: string) => {
    setSelectedLineItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectedLineItemIdsArray = useMemo(
    () => Array.from(selectedLineItemIds),
    [selectedLineItemIds]
  );

  if (state.kind === 'loading') {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center justify-center p-4 text-brand-600">
        <Spinner size="lg" label="Loading quote" />
      </main>
    );
  }

  if (state.kind === 'expired') {
    return <ExpiredOrInvalid />;
  }

  if (state.kind === 'error') {
    return (
      <main className="mx-auto flex min-h-screen max-w-xl items-center justify-center p-4">
        <Card className="w-full border-l-4 border-red-200 bg-red-50 text-center">
          <h1 className="text-lg font-semibold text-red-900">We couldn&apos;t load this quote</h1>
          <p className="mt-2 text-sm text-red-800">{state.message}</p>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </div>
        </Card>
      </main>
    );
  }

  const { data } = state;
  const region = resolveRegionSafe(data.company.country);
  const isPending = data.quote.status === 'PENDING';
  const hasTerms =
    (data.termsAndConditions.standardTermsHtml ?? '').trim() !== '' ||
    (data.termsAndConditions.additionalTerms ?? '').trim() !== '';

  return (
    <main className="mx-auto max-w-3xl space-y-4 p-4 sm:py-8">
      <Card>
        <QuoteHeader company={data.company} quote={data.quote} />
      </Card>

      <Card>
        <LineItems
          lineItems={data.lineItems}
          totals={data.totals}
          pricesIncludeTax={data.company.pricesIncludeTax}
          isPending={isPending}
          selectedLineItemIds={selectedLineItemIds}
          onToggleItem={handleToggleItem}
          currency={region.currency}
          locale={region.locale}
        />
      </Card>

      {hasTerms ? (
        <Card>
          <h2 className="text-base font-semibold text-slate-900">Terms &amp; conditions</h2>
          {data.termsAndConditions.standardTermsHtml ? (
            <RichText className="mt-2" html={data.termsAndConditions.standardTermsHtml} />
          ) : null}
          {data.termsAndConditions.additionalTerms ? (
            <>
              <h3 className="mt-4 text-sm font-semibold text-slate-800">Additional terms</h3>
              <RichText className="mt-1" html={data.termsAndConditions.additionalTerms} />
            </>
          ) : null}
        </Card>
      ) : null}

      {data.attachments.length > 0 ? (
        <Card>
          <Attachments attachments={data.attachments} />
        </Card>
      ) : null}

      {isPending ? (
        <Card>
          <h2 className="text-base font-semibold text-slate-900">Your response</h2>
          <p className="mt-1 text-sm text-slate-600">
            Choose how you would like to respond to this quote.
          </p>

          {activeMode === null ? (
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <Button onClick={() => setActiveMode('accept')}>Accept</Button>
              <Button variant="secondary" onClick={() => setActiveMode('request_changes')}>
                Request changes
              </Button>
              <Button variant="danger" onClick={() => setActiveMode('reject')}>
                Decline
              </Button>
            </div>
          ) : (
            token && (
              <ResponseForm
                token={token}
                mode={activeMode}
                selectedLineItemIds={selectedLineItemIdsArray}
                onCancel={() => setActiveMode(null)}
                onSuccess={(result) => {
                  navigate(`/quotes/${encodeURIComponent(token)}/confirmation`, {
                    state: { outcome: result.outcome },
                    replace: true,
                  });
                }}
              />
            )
          )}
        </Card>
      ) : (
        <AlreadyResponded status={data.quote.status} />
      )}
    </main>
  );
};

export default QuoteView;
